import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const client = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach the bearer token (if logged in) to every request.
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On an expired/invalid token, drop it and bounce back to the login screen.
client.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config && error.config.url ? error.config.url : '';
    const isLogin = url.includes('/auth/login');
    if (error.response && error.response.status === 401 && !isLogin) {
      localStorage.removeItem('token');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Collection endpoints use a trailing slash to match FastAPI's "/" routes
// directly. Without it, FastAPI 307-redirects to the slash version, which
// (behind Nginx) lands on the React app and returns HTML instead of JSON.

// Tours
export const tourApi = {
  list: () => client.get('/tours/'),
  get: (id) => client.get(`/tours/${id}`),
  create: (data) => client.post('/tours/', data),
  update: (id, data) => client.put(`/tours/${id}`, data),
  delete: (id) => client.delete(`/tours/${id}`),
};

// Tour Legs
export const tourLegApi = {
  list: (tourId) => client.get('/tour_legs/', { params: { tour_id: tourId } }),
  get: (id) => client.get(`/tour_legs/${id}`),
  create: (data) => client.post('/tour_legs/', data),
  update: (id, data) => client.put(`/tour_legs/${id}`, data),
  delete: (id) => client.delete(`/tour_legs/${id}`),
};

// Transport Legs (reisonderdelen). Read-only: they are created through the
// import, not through the API.
export const transportLegApi = {
  list: (params) => client.get('/transport_legs/', { params }),
  get: (id) => client.get(`/transport_legs/${id}`),
};

// Manual price observations. Written to price_snapshot with
// source = 'manual'. Replaced offerApi, which wrote to the dropped
// train_trip_offer.
export const observationApi = {
  list: (params) => client.get('/observations/', { params }),
  sources: () => client.get('/observations/sources'),
  create: (data) => client.post('/observations/', data),
  delete: (id) => client.delete(`/observations/${id}`),
};

// Transport Companies
export const transportCompanyApi = {
  list: () => client.get('/transport_companies/'),
  get: (id) => client.get(`/transport_companies/${id}`),
  create: (data) => client.post('/transport_companies/', data),
};

// Insights window (task 1.8). Read-only: this screen is a window, not a door.
export const insightsApi = {
  overview: () => client.get('/insights/overview'),
  packages: () => client.get('/insights/packages'),
  quotes: (params) => client.get('/insights/quotes', { params }),
  quote: (id) => client.get(`/insights/quotes/${id}`),
  legs: () => client.get('/insights/legs'),
  priceHistory: (id, params) =>
    client.get(`/insights/price_history/${id}`, { params }),
};

// Hotel bookings at RateHawk (tasks 1.23 and 1.24). Sandbox only for now:
// the backend refuses to book anywhere else.
export const hotelBookingApi = {
  hotels: () => client.get('/hotel_bookings/hotels'),
  tours: () => client.get('/hotel_bookings/tours'),
  rates: (query) => client.post('/hotel_bookings/rates', query),
  prebook: (data) => client.post('/hotel_bookings/prebook', data),
  book: (id, data) => client.post(`/hotel_bookings/${id}/book`, data),
  status: (id) => client.get(`/hotel_bookings/${id}/status`),
  order: (id) => client.get(`/hotel_bookings/${id}/order`),
  cancel: (id) => client.post(`/hotel_bookings/${id}/cancel`),
  list: () => client.get('/hotel_bookings/'),
  get: (id) => client.get(`/hotel_bookings/${id}`),
};

// Rail prices (tasks 2.1c and 2.1d). The backend picks the source from the
// company of the reisonderdeel; today only Eurostar is live.
export const railPriceApi = {
  liveCompanies: () => client.get('/rail_prices/live_companies'),
  search: (query) => client.post('/rail_prices/search', query),
};

// Booking a train from the rail price screen (task 4.21)
export const railBookingApi = {
  list: () => client.get('/rail_bookings'),
  create: (booking) => client.post('/rail_bookings', booking),
  // After the booking. The last name goes in the body, never in the address.
  passengers: (id, lastName) => client.post(`/rail_bookings/${id}/passengers`, { last_name: lastName }),
  contacts: (id, lastName, emails) => client.post(`/rail_bookings/${id}/contacts`, { last_name: lastName, emails }),
  travelDocument: (id, lastName, document) =>
    client.post(`/rail_bookings/${id}/travel_documents`, { last_name: lastName, ...document }),
  tickets: (id, lastName, language = 'nl') =>
    client.post(`/rail_bookings/${id}/tickets`, { last_name: lastName, language }),
  refundQuote: (id, lastName) => client.post(`/rail_bookings/${id}/refund_quote`, { last_name: lastName }),
  refund: (id, lastName, acceptRefund) =>
    client.post(`/rail_bookings/${id}/refund`, { last_name: lastName, accept_refund: acceptRefund }),
  void: (id, lastName) => client.post(`/rail_bookings/${id}/void`, { last_name: lastName }),
  exchangeOffers: (id, lastName, travelDate) =>
    client.post(`/rail_bookings/${id}/exchange_offers`, { last_name: lastName, travel_date: travelDate }),
  exchange: (id, lastName, offer, acceptDifference) =>
    client.post(`/rail_bookings/${id}/exchange`, {
      last_name: lastName,
      travel_date: offer.travel_date,
      service_name: offer.service_name,
      fare_class: offer.fare_class,
      accept_difference: acceptDifference,
    }),
};

// Auth
export const authApi = {
  login: (email, password) => client.post('/auth/login', { email, password }),
  me: () => client.get('/auth/me'),
};

export default client;
